// QuickFilter.js
import React, { useState, useCallback } from "react";
import {
  Typography,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

export default function QuickFilter({
  selectedRuleFlowGroup,
  onRuleFlowGroupChange,
  policyKeys = [],
  selectedPolicyKey,
  onPolicyKeyChange,
  columnOptions = [],
  selectedColumns,
  onColumnChange,
  activeOnly,
  onActiveOnlyChange,
  onAccordionToggle,
}) {
  /* ---------------- state & helpers ---------------- */

  const [expanded, setExpanded] = useState(true);

  const toggle = (_e, is) => {
    setExpanded(is);
    onAccordionToggle?.(is);
  };

  // remove a selected column
  const removeCol = useCallback(
    (col) =>
      onColumnChange({
        target: { value: selectedColumns.filter((c) => c !== col) },
      }),
    [selectedColumns, onColumnChange]
  );

  const handleReset = () => {
    onRuleFlowGroupChange("");
    onPolicyKeyChange("");
    onColumnChange({ target: { value: [] } });
    onActiveOnlyChange(true);
  };

  const showReset =
    selectedRuleFlowGroup ||
    selectedPolicyKey ||
    selectedColumns.length ||
    activeOnly === false;

  // Helper function to get display label for policy keys
  const getPolicyKeyLabel = (key) => {
    if (key === null) return "No Policy";
    return key;
  };

  /* ------------------- render ---------------------- */

  return (
    <Accordion
      sx={{
        ".MuiAccordionSummary-content.Mui-expanded": {
          margin: "0px",
        },
      }}
      expanded={expanded}
      onChange={toggle}
    >
      {/* summary bar */}
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        sx={{
          "& .MuiAccordionSummary-content": {
            marginLeft: 0,
          },
          paddingLeft: 0,
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ mb: 0, pl: 1, pr: 1 }}
        >
          <Typography variant="h6" sx={{ fontSize: 20, fontWeight: "bold" }}>
            Quick Filters:
          </Typography>

          {showReset && (
            <Button
              component="div"
              onClick={handleReset}
              sx={{ p: 0, minWidth: 0 }}
            >
              Reset
            </Button>
          )}

          {!expanded && selectedRuleFlowGroup && (
            <Typography variant="body2">
              RFG: {selectedRuleFlowGroup}
            </Typography>
          )}
          {!expanded && selectedPolicyKey && (
            <Typography variant="body2">
              Policy Key: {getPolicyKeyLabel(selectedPolicyKey)}
            </Typography>
          )}
          {!expanded && activeOnly && (
            <Typography variant="body2">Active Only</Typography>
          )}
        </Stack>
      </AccordionSummary>

      {/* expanded content */}
      <AccordionDetails>
        {/* Policy‑key chips */}
        <Stack
          direction="row"
          spacing={1}
          flexWrap="wrap"
          rowGap={1}
          sx={{ mb: 1 }}
        >
          <Typography
            variant="subtitle1"
            sx={{ width: 125, fontWeight: "bold" }}
          >
            Policy Key:
          </Typography>
          {policyKeys.map((k) => {
            const lbl = getPolicyKeyLabel(k);
            return (
              <Chip
                key={k}
                label={lbl}
                onClick={() => onPolicyKeyChange(k)}
                color={selectedPolicyKey === k ? "primary" : "default"}
                sx={{ minWidth: 100 }}
              />
            );
          })}
        </Stack>

        {/* Column selector */}
        <Stack spacing={1} sx={{ mb: 2 }}>
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel id="columns-label">Columns</InputLabel>
            <Select
              labelId="columns-label"
              multiple
              value={selectedColumns}
              onChange={onColumnChange}
              input={<OutlinedInput label="Columns" />}
              renderValue={(sel) => sel.join(", ")}
            >
              {columnOptions.map((col) => (
                <MenuItem key={col} value={col}>
                  <Checkbox checked={selectedColumns.includes(col)} />
                  <ListItemText primary={col} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* selected‑column chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
            {selectedColumns.map((col) => (
              <Chip
                key={col}
                label={col}
                onDelete={() => removeCol(col)}
                color="primary"
                sx={{ minWidth: 125 }}
              />
            ))}
          </Stack>
        </Stack>

        {/* Active‑only toggle */}
        <Stack direction="row" spacing={1} flexWrap="wrap" rowGap={1}>
          <Typography
            variant="subtitle1"
            sx={{ width: 125, fontWeight: "bold" }}
          >
            Active Only:
          </Typography>
          {[
            { label: "Yes", value: true },
            { label: "No", value: false },
          ].map((opt) => (
            <Chip
              key={opt.label}
              label={opt.label}
              onClick={() => onActiveOnlyChange(opt.value)}
              color={activeOnly === opt.value ? "primary" : "default"}
              sx={{ minWidth: 125 }}
            />
          ))}
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
}
